// ISO 4217 Currency Definitions & Curated Selection for Timelining

export interface IsoCurrency {
  code: string
  name: string
  symbol: string
  flag: string
}

/**
 * Curated 20 currencies ordered by usage weight + geographic spread per §2.4:
 * USD, EUR, GBP, INR, JPY, CNY, AUD, CAD, SGD, AED, CHF, HKD, KRW, BRL, MXN, ZAR, NZD, SEK, NOK, DKK
 */
export const CURATED_CURRENCY_CODES = [
  "USD", "EUR", "GBP", "INR", "JPY",
  "CNY", "AUD", "CAD", "SGD", "AED",
  "CHF", "HKD", "KRW", "BRL", "MXN",
  "ZAR", "NZD", "SEK", "NOK", "DKK",
] as const

export const ISO_4217_CURRENCIES: IsoCurrency[] = [
  {
    "code": "AED",
    "name": "United Arab Emirates Dirham",
    "symbol": "د.إ",
    "flag": "🇦🇪"
  },
  {
    "code": "AFN",
    "name": "Afghan Afghani",
    "symbol": "؋",
    "flag": "🇦🇫"
  },
  {
    "code": "ALL",
    "name": "Albanian Lek",
    "symbol": "ALL",
    "flag": "🇦🇱"
  },
  {
    "code": "AMD",
    "name": "Armenian Dram",
    "symbol": "֏",
    "flag": "🇦🇲"
  },
  {
    "code": "ANG",
    "name": "Netherlands Antillean Guilder",
    "symbol": "ANG",
    "flag": "🇨🇼"
  },
  {
    "code": "AOA",
    "name": "Angolan Kwanza",
    "symbol": "Kz",
    "flag": "🇦🇴"
  },
  {
    "code": "ARS",
    "name": "Argentine Peso",
    "symbol": "$",
    "flag": "🇦🇷"
  },
  {
    "code": "AUD",
    "name": "Australian Dollar",
    "symbol": "A$",
    "flag": "🇦🇺"
  },
  {
    "code": "AWG",
    "name": "Aruban Florin",
    "symbol": "AWG",
    "flag": "🇦🇼"
  },
  {
    "code": "AZN",
    "name": "Azerbaijani Manat",
    "symbol": "₼",
    "flag": "🇦🇿"
  },
  {
    "code": "BAM",
    "name": "Bosnia-Herzegovina Convertible Mark",
    "symbol": "KM",
    "flag": "🇧🇦"
  },
  {
    "code": "BBD",
    "name": "Barbadian Dollar",
    "symbol": "$",
    "flag": "🇧🇧"
  },
  {
    "code": "BDT",
    "name": "Bangladeshi Taka",
    "symbol": "৳",
    "flag": "🇧🇩"
  },
  {
    "code": "BGN",
    "name": "Bulgarian Lev",
    "symbol": "BGN",
    "flag": "🇧🇬"
  },
  {
    "code": "BHD",
    "name": "Bahraini Dinar",
    "symbol": "BHD",
    "flag": "🇧🇭"
  },
  {
    "code": "BIF",
    "name": "Burundian Franc",
    "symbol": "BIF",
    "flag": "🇧🇮"
  },
  {
    "code": "BMD",
    "name": "Bermudan Dollar",
    "symbol": "$",
    "flag": "🇧🇲"
  },
  {
    "code": "BND",
    "name": "Brunei Dollar",
    "symbol": "$",
    "flag": "🇧🇳"
  },
  {
    "code": "BOB",
    "name": "Bolivian Boliviano",
    "symbol": "Bs",
    "flag": "🇧🇴"
  },
  {
    "code": "BRL",
    "name": "Brazilian Real",
    "symbol": "R$",
    "flag": "🇧🇷"
  },
  {
    "code": "BSD",
    "name": "Bahamian Dollar",
    "symbol": "$",
    "flag": "🇧🇸"
  },
  {
    "code": "BTN",
    "name": "Bhutanese Ngultrum",
    "symbol": "BTN",
    "flag": "🇧🇹"
  },
  {
    "code": "BWP",
    "name": "Botswanan Pula",
    "symbol": "P",
    "flag": "🇧🇼"
  },
  {
    "code": "BYN",
    "name": "Belarusian Ruble",
    "symbol": "BYN",
    "flag": "🇧🇾"
  },
  {
    "code": "BZD",
    "name": "Belize Dollar",
    "symbol": "$",
    "flag": "🇧🇿"
  },
  {
    "code": "CAD",
    "name": "Canadian Dollar",
    "symbol": "C$",
    "flag": "🇨🇦"
  },
  {
    "code": "CDF",
    "name": "Congolese Franc",
    "symbol": "CDF",
    "flag": "🇨🇩"
  },
  {
    "code": "CHF",
    "name": "Swiss Franc",
    "symbol": "CHF",
    "flag": "🇨🇭"
  },
  {
    "code": "CLP",
    "name": "Chilean Peso",
    "symbol": "$",
    "flag": "🇨🇱"
  },
  {
    "code": "CNY",
    "name": "Chinese Yuan",
    "symbol": "¥",
    "flag": "🇨🇳"
  },
  {
    "code": "COP",
    "name": "Colombian Peso",
    "symbol": "$",
    "flag": "🇨🇴"
  },
  {
    "code": "CRC",
    "name": "Costa Rican Colón",
    "symbol": "₡",
    "flag": "🇨🇷"
  },
  {
    "code": "CUC",
    "name": "Cuban Convertible Peso",
    "symbol": "$",
    "flag": "🇨🇺"
  },
  {
    "code": "CUP",
    "name": "Cuban Peso",
    "symbol": "$",
    "flag": "🇨🇺"
  },
  {
    "code": "CVE",
    "name": "Cape Verdean Escudo",
    "symbol": "CVE",
    "flag": "🇨🇻"
  },
  {
    "code": "CZK",
    "name": "Czech Koruna",
    "symbol": "Kč",
    "flag": "🇨🇿"
  },
  {
    "code": "DJF",
    "name": "Djiboutian Franc",
    "symbol": "DJF",
    "flag": "🇩🇯"
  },
  {
    "code": "DKK",
    "name": "Danish Krone",
    "symbol": "kr",
    "flag": "🇩🇰"
  },
  {
    "code": "DOP",
    "name": "Dominican Peso",
    "symbol": "$",
    "flag": "🇩🇴"
  },
  {
    "code": "DZD",
    "name": "Algerian Dinar",
    "symbol": "DZD",
    "flag": "🇩🇿"
  },
  {
    "code": "EGP",
    "name": "Egyptian Pound",
    "symbol": "E£",
    "flag": "🇪🇬"
  },
  {
    "code": "ERN",
    "name": "Eritrean Nakfa",
    "symbol": "ERN",
    "flag": "🇪🇷"
  },
  {
    "code": "ETB",
    "name": "Ethiopian Birr",
    "symbol": "ETB",
    "flag": "🇪🇹"
  },
  {
    "code": "EUR",
    "name": "Euro",
    "symbol": "€",
    "flag": "🇪🇺"
  },
  {
    "code": "FJD",
    "name": "Fijian Dollar",
    "symbol": "$",
    "flag": "🇫🇯"
  },
  {
    "code": "FKP",
    "name": "Falkland Islands Pound",
    "symbol": "£",
    "flag": "🇫🇰"
  },
  {
    "code": "GBP",
    "name": "British Pound",
    "symbol": "£",
    "flag": "🇬🇧"
  },
  {
    "code": "GEL",
    "name": "Georgian Lari",
    "symbol": "₾",
    "flag": "🇬🇪"
  },
  {
    "code": "GHS",
    "name": "Ghanaian Cedi",
    "symbol": "GH₵",
    "flag": "🇬🇭"
  },
  {
    "code": "GIP",
    "name": "Gibraltar Pound",
    "symbol": "£",
    "flag": "🇬🇮"
  },
  {
    "code": "GMD",
    "name": "Gambian Dalasi",
    "symbol": "GMD",
    "flag": "🇬🇲"
  },
  {
    "code": "GNF",
    "name": "Guinean Franc",
    "symbol": "FG",
    "flag": "🇬🇳"
  },
  {
    "code": "GTQ",
    "name": "Guatemalan Quetzal",
    "symbol": "Q",
    "flag": "🇬🇹"
  },
  {
    "code": "GYD",
    "name": "Guyanaese Dollar",
    "symbol": "$",
    "flag": "🇬🇾"
  },
  {
    "code": "HKD",
    "name": "Hong Kong Dollar",
    "symbol": "HK$",
    "flag": "🇭🇰"
  },
  {
    "code": "HNL",
    "name": "Honduran Lempira",
    "symbol": "L",
    "flag": "🇭🇳"
  },
  {
    "code": "HRK",
    "name": "Croatian Kuna",
    "symbol": "kn",
    "flag": "🇭🇷"
  },
  {
    "code": "HTG",
    "name": "Haitian Gourde",
    "symbol": "HTG",
    "flag": "🇭🇹"
  },
  {
    "code": "HUF",
    "name": "Hungarian Forint",
    "symbol": "Ft",
    "flag": "🇭🇺"
  },
  {
    "code": "IDR",
    "name": "Indonesian Rupiah",
    "symbol": "Rp",
    "flag": "🇮🇩"
  },
  {
    "code": "ILS",
    "name": "Israeli New Shekel",
    "symbol": "₪",
    "flag": "🇮🇱"
  },
  {
    "code": "INR",
    "name": "Indian Rupee",
    "symbol": "₹",
    "flag": "🇮🇳"
  },
  {
    "code": "IQD",
    "name": "Iraqi Dinar",
    "symbol": "IQD",
    "flag": "🇮🇶"
  },
  {
    "code": "IRR",
    "name": "Iranian Rial",
    "symbol": "IRR",
    "flag": "🇮🇷"
  },
  {
    "code": "ISK",
    "name": "Icelandic Króna",
    "symbol": "kr",
    "flag": "🇮🇸"
  },
  {
    "code": "JMD",
    "name": "Jamaican Dollar",
    "symbol": "$",
    "flag": "🇯🇲"
  },
  {
    "code": "JOD",
    "name": "Jordanian Dinar",
    "symbol": "JOD",
    "flag": "🇯🇴"
  },
  {
    "code": "JPY",
    "name": "Japanese Yen",
    "symbol": "¥",
    "flag": "🇯🇵"
  },
  {
    "code": "KES",
    "name": "Kenyan Shilling",
    "symbol": "KES",
    "flag": "🇰🇪"
  },
  {
    "code": "KGS",
    "name": "Kyrgyz Som",
    "symbol": "⃀",
    "flag": "🇰🇬"
  },
  {
    "code": "KHR",
    "name": "Cambodian Riel",
    "symbol": "៛",
    "flag": "🇰🇭"
  },
  {
    "code": "KMF",
    "name": "Comorian Franc",
    "symbol": "CF",
    "flag": "🇰🇲"
  },
  {
    "code": "KPW",
    "name": "North Korean Won",
    "symbol": "₩",
    "flag": "🇰🇵"
  },
  {
    "code": "KRW",
    "name": "South Korean Won",
    "symbol": "₩",
    "flag": "🇰🇷"
  },
  {
    "code": "KWD",
    "name": "Kuwaiti Dinar",
    "symbol": "KWD",
    "flag": "🇰🇼"
  },
  {
    "code": "KYD",
    "name": "Cayman Islands Dollar",
    "symbol": "$",
    "flag": "🇰🇾"
  },
  {
    "code": "KZT",
    "name": "Kazakhstani Tenge",
    "symbol": "₸",
    "flag": "🇰🇿"
  },
  {
    "code": "LAK",
    "name": "Laotian Kip",
    "symbol": "₭",
    "flag": "🇱🇦"
  },
  {
    "code": "LBP",
    "name": "Lebanese Pound",
    "symbol": "L£",
    "flag": "🇱🇧"
  },
  {
    "code": "LKR",
    "name": "Sri Lankan Rupee",
    "symbol": "Rs",
    "flag": "🇱🇰"
  },
  {
    "code": "LRD",
    "name": "Liberian Dollar",
    "symbol": "$",
    "flag": "🇱🇷"
  },
  {
    "code": "LSL",
    "name": "Lesotho Loti",
    "symbol": "LSL",
    "flag": "🇱🇸"
  },
  {
    "code": "LYD",
    "name": "Libyan Dinar",
    "symbol": "LYD",
    "flag": "🇱🇾"
  },
  {
    "code": "MAD",
    "name": "Moroccan Dirham",
    "symbol": "MAD",
    "flag": "🇲🇦"
  },
  {
    "code": "MDL",
    "name": "Moldovan Leu",
    "symbol": "MDL",
    "flag": "🇲🇩"
  },
  {
    "code": "MGA",
    "name": "Malagasy Ariary",
    "symbol": "Ar",
    "flag": "🇲🇬"
  },
  {
    "code": "MKD",
    "name": "Macedonian Denar",
    "symbol": "MKD",
    "flag": "🇲🇰"
  },
  {
    "code": "MMK",
    "name": "Myanmar Kyat",
    "symbol": "K",
    "flag": "🇲🇲"
  },
  {
    "code": "MNT",
    "name": "Mongolian Tugrik",
    "symbol": "₮",
    "flag": "🇲🇳"
  },
  {
    "code": "MOP",
    "name": "Macanese Pataca",
    "symbol": "MOP",
    "flag": "🇲🇴"
  },
  {
    "code": "MRU",
    "name": "Mauritanian Ouguiya",
    "symbol": "MRU",
    "flag": "🇲🇷"
  },
  {
    "code": "MUR",
    "name": "Mauritian Rupee",
    "symbol": "Rs",
    "flag": "🇲🇺"
  },
  {
    "code": "MVR",
    "name": "Maldivian Rufiyaa",
    "symbol": "MVR",
    "flag": "🇲🇻"
  },
  {
    "code": "MWK",
    "name": "Malawian Kwacha",
    "symbol": "MWK",
    "flag": "🇲🇼"
  },
  {
    "code": "MXN",
    "name": "Mexican Peso",
    "symbol": "$",
    "flag": "🇲🇽"
  },
  {
    "code": "MYR",
    "name": "Malaysian Ringgit",
    "symbol": "RM",
    "flag": "🇲🇾"
  },
  {
    "code": "MZN",
    "name": "Mozambican Metical",
    "symbol": "MZN",
    "flag": "🇲🇿"
  },
  {
    "code": "NAD",
    "name": "Namibian Dollar",
    "symbol": "$",
    "flag": "🇳🇦"
  },
  {
    "code": "NGN",
    "name": "Nigerian Naira",
    "symbol": "₦",
    "flag": "🇳🇬"
  },
  {
    "code": "NIO",
    "name": "Nicaraguan Córdoba",
    "symbol": "C$",
    "flag": "🇳🇮"
  },
  {
    "code": "NOK",
    "name": "Norwegian Krone",
    "symbol": "kr",
    "flag": "🇳🇴"
  },
  {
    "code": "NPR",
    "name": "Nepalese Rupee",
    "symbol": "Rs",
    "flag": "🇳🇵"
  },
  {
    "code": "NZD",
    "name": "New Zealand Dollar",
    "symbol": "NZ$",
    "flag": "🇳🇿"
  },
  {
    "code": "OMR",
    "name": "Omani Rial",
    "symbol": "OMR",
    "flag": "🇴🇲"
  },
  {
    "code": "PAB",
    "name": "Panamanian Balboa",
    "symbol": "PAB",
    "flag": "🇵🇦"
  },
  {
    "code": "PEN",
    "name": "Peruvian Sol",
    "symbol": "PEN",
    "flag": "🇵🇪"
  },
  {
    "code": "PGK",
    "name": "Papua New Guinean Kina",
    "symbol": "PGK",
    "flag": "🇵🇬"
  },
  {
    "code": "PHP",
    "name": "Philippine Peso",
    "symbol": "₱",
    "flag": "🇵🇭"
  },
  {
    "code": "PKR",
    "name": "Pakistani Rupee",
    "symbol": "Rs",
    "flag": "🇵🇰"
  },
  {
    "code": "PLN",
    "name": "Polish Zloty",
    "symbol": "zł",
    "flag": "🇵🇱"
  },
  {
    "code": "PYG",
    "name": "Paraguayan Guarani",
    "symbol": "₲",
    "flag": "🇵🇾"
  },
  {
    "code": "QAR",
    "name": "Qatari Riyal",
    "symbol": "QAR",
    "flag": "🇶🇦"
  },
  {
    "code": "RON",
    "name": "Romanian Leu",
    "symbol": "lei",
    "flag": "🇷🇴"
  },
  {
    "code": "RSD",
    "name": "Serbian Dinar",
    "symbol": "RSD",
    "flag": "🇷🇸"
  },
  {
    "code": "RUB",
    "name": "Russian Ruble",
    "symbol": "₽",
    "flag": "🇷🇺"
  },
  {
    "code": "RWF",
    "name": "Rwandan Franc",
    "symbol": "RF",
    "flag": "🇷🇼"
  },
  {
    "code": "SAR",
    "name": "Saudi Riyal",
    "symbol": "SAR",
    "flag": "🇸🇦"
  },
  {
    "code": "SBD",
    "name": "Solomon Islands Dollar",
    "symbol": "$",
    "flag": "🇸🇧"
  },
  {
    "code": "SCR",
    "name": "Seychellois Rupee",
    "symbol": "SCR",
    "flag": "🇸🇨"
  },
  {
    "code": "SDG",
    "name": "Sudanese Pound",
    "symbol": "SDG",
    "flag": "🇸🇩"
  },
  {
    "code": "SEK",
    "name": "Swedish Krona",
    "symbol": "kr",
    "flag": "🇸🇪"
  },
  {
    "code": "SGD",
    "name": "Singapore Dollar",
    "symbol": "S$",
    "flag": "🇸🇬"
  },
  {
    "code": "SHP",
    "name": "St. Helena Pound",
    "symbol": "£",
    "flag": "🇸🇭"
  },
  {
    "code": "SLE",
    "name": "Sierra Leonean Leone",
    "symbol": "SLE",
    "flag": "🇸🇱"
  },
  {
    "code": "SLL",
    "name": "Sierra Leonean Leone (1964—2022)",
    "symbol": "SLL",
    "flag": "🇸🇱"
  },
  {
    "code": "SOS",
    "name": "Somali Shilling",
    "symbol": "SOS",
    "flag": "🇸🇴"
  },
  {
    "code": "SRD",
    "name": "Surinamese Dollar",
    "symbol": "$",
    "flag": "🇸🇷"
  },
  {
    "code": "SSP",
    "name": "South Sudanese Pound",
    "symbol": "£",
    "flag": "🇸🇸"
  },
  {
    "code": "STN",
    "name": "São Tomé & Príncipe Dobra",
    "symbol": "Db",
    "flag": "🇸🇹"
  },
  {
    "code": "SVC",
    "name": "Salvadoran Colón",
    "symbol": "SVC",
    "flag": "🇸🇻"
  },
  {
    "code": "SYP",
    "name": "Syrian Pound",
    "symbol": "£",
    "flag": "🇸🇾"
  },
  {
    "code": "SZL",
    "name": "Swazi Lilangeni",
    "symbol": "SZL",
    "flag": "🇸🇿"
  },
  {
    "code": "THB",
    "name": "Thai Baht",
    "symbol": "฿",
    "flag": "🇹🇭"
  },
  {
    "code": "TJS",
    "name": "Tajikistani Somoni",
    "symbol": "TJS",
    "flag": "🇹🇯"
  },
  {
    "code": "TMT",
    "name": "Turkmenistani Manat",
    "symbol": "TMT",
    "flag": "🇹🇲"
  },
  {
    "code": "TND",
    "name": "Tunisian Dinar",
    "symbol": "TND",
    "flag": "🇹🇳"
  },
  {
    "code": "TOP",
    "name": "Tongan Paʻanga",
    "symbol": "T$",
    "flag": "🇹🇴"
  },
  {
    "code": "TRY",
    "name": "Turkish Lira",
    "symbol": "₺",
    "flag": "🇹🇷"
  },
  {
    "code": "TTD",
    "name": "Trinidad & Tobago Dollar",
    "symbol": "$",
    "flag": "🇹🇹"
  },
  {
    "code": "TWD",
    "name": "New Taiwan Dollar",
    "symbol": "$",
    "flag": "🇹🇼"
  },
  {
    "code": "TZS",
    "name": "Tanzanian Shilling",
    "symbol": "TZS",
    "flag": "🇹🇿"
  },
  {
    "code": "UAH",
    "name": "Ukrainian Hryvnia",
    "symbol": "₴",
    "flag": "🇺🇦"
  },
  {
    "code": "UGX",
    "name": "Ugandan Shilling",
    "symbol": "UGX",
    "flag": "🇺🇬"
  },
  {
    "code": "USD",
    "name": "US Dollar",
    "symbol": "$",
    "flag": "🇺🇸"
  },
  {
    "code": "UYU",
    "name": "Uruguayan Peso",
    "symbol": "$",
    "flag": "🇺🇾"
  },
  {
    "code": "UZS",
    "name": "Uzbekistani Som",
    "symbol": "UZS",
    "flag": "🇺🇿"
  },
  {
    "code": "VES",
    "name": "Venezuelan Bolívar",
    "symbol": "VES",
    "flag": "🇻🇪"
  },
  {
    "code": "VND",
    "name": "Vietnamese Dong",
    "symbol": "₫",
    "flag": "🇻🇳"
  },
  {
    "code": "VUV",
    "name": "Vanuatu Vatu",
    "symbol": "VUV",
    "flag": "🇻🇺"
  },
  {
    "code": "WST",
    "name": "Samoan Tala",
    "symbol": "WST",
    "flag": "🇼🇸"
  },
  {
    "code": "XAF",
    "name": "Central African CFA Franc",
    "symbol": "FCFA",
    "flag": "🇨🇲"
  },
  {
    "code": "XCD",
    "name": "East Caribbean Dollar",
    "symbol": "$",
    "flag": "🇦🇬"
  },
  {
    "code": "XCG",
    "name": "Caribbean guilder",
    "symbol": "Cg.",
    "flag": "🇽🇨"
  },
  {
    "code": "XDR",
    "name": "Special Drawing Rights",
    "symbol": "XDR",
    "flag": "🇽🇩"
  },
  {
    "code": "XOF",
    "name": "West African CFA Franc",
    "symbol": "F CFA",
    "flag": "🇸🇳"
  },
  {
    "code": "XPF",
    "name": "CFP Franc",
    "symbol": "CFPF",
    "flag": "🇵🇫"
  },
  {
    "code": "XSU",
    "name": "Sucre",
    "symbol": "XSU",
    "flag": "🇽🇸"
  },
  {
    "code": "YER",
    "name": "Yemeni Rial",
    "symbol": "YER",
    "flag": "🇾🇪"
  },
  {
    "code": "ZAR",
    "name": "South African Rand",
    "symbol": "R",
    "flag": "🇿🇦"
  },
  {
    "code": "ZMW",
    "name": "Zambian Kwacha",
    "symbol": "ZK",
    "flag": "🇿🇲"
  },
  {
    "code": "ZWG",
    "name": "Zimbabwean Gold",
    "symbol": "ZWG",
    "flag": "🇿🇼"
  },
  {
    "code": "ZWL",
    "name": "Zimbabwean Dollar (2009–2024)",
    "symbol": "ZWL",
    "flag": "🇿🇼"
  }
]

const CURRENCY_MAP = new Map<string, IsoCurrency>(
  ISO_4217_CURRENCIES.map((c) => [c.code.toUpperCase(), c]),
)

export const getIsoCurrency = (code: string | null | undefined): IsoCurrency | undefined => {
  if (!code) return undefined
  return CURRENCY_MAP.get(code.trim().toUpperCase())
}

export const ISO_4217_MINOR_UNIT_DIGITS: Record<string, number> = {
  // 0 fraction digits
  BIF: 0,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  ISK: 0,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  PYG: 0,
  RWF: 0,
  UGX: 0,
  UYI: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
  // 3 fraction digits
  BHD: 3,
  IQD: 3,
  JOD: 3,
  KWD: 3,
  LYD: 3,
  OMR: 3,
  TND: 3,
  // 4 fraction digits
  CLF: 4,
  UYW: 4,
}

export const getCurrencyMinorUnitDigits = (currency: string | null | undefined): number => {
  if (!currency || typeof currency !== 'string') return 2
  const code = currency.trim().toUpperCase()
  return ISO_4217_MINOR_UNIT_DIGITS[code] ?? 2
}

